import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Switch, Checkbox } from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";

import TimeRangeSelector from "components/CheckInCheckOut/TimeRangeSelector";
import Text from "components/common/Text";

import { DAYS_ORDER } from "components/Screens/Timesheet/helpers";

import "components/CheckInCheckOut/WeeklyShiftSelector/WeeklyShiftSelector.scss";

function WeeklyShiftSelector({
  weekSchedule,
  onWeekScheduleChange,
  sameForAllDays = false,
  onSameForAllDaysChange,
  helperText = "Please enter all times in your local timezone.",
  showHelperText = true,
  disabled = false,
}) {
  const [localSchedule, setLocalSchedule] = useState(weekSchedule);

  useEffect(() => {
    if (weekSchedule) {
      setLocalSchedule(weekSchedule);
    }
  }, [weekSchedule]);

  const handleDayToggle = (dayKey) => {
    if (!localSchedule[dayKey]) return;

    const updatedSchedule = {
      ...localSchedule,
      [dayKey]: {
        ...localSchedule[dayKey],
        enabled: !localSchedule[dayKey].enabled,
      },
    };
    setLocalSchedule(updatedSchedule);
    onWeekScheduleChange(updatedSchedule);
  };

  const handleTimeChange = (dayKey, field, value) => {
    if (!localSchedule || !localSchedule[dayKey]) return;

    const updatedSchedule = { ...localSchedule };

    if (sameForAllDays) {
      DAYS_ORDER.forEach((day) => {
        if (updatedSchedule[day.key]?.enabled) {
          updatedSchedule[day.key] = {
            ...updatedSchedule[day.key],
            [field]: value,
          };
        }
      });
    } else {
      updatedSchedule[dayKey] = {
        ...updatedSchedule[dayKey],
        [field]: value,
      };
    }

    setLocalSchedule(updatedSchedule);
    onWeekScheduleChange(updatedSchedule);
  };

  const handleSameForAllDaysChange = (event) => {
    const { checked } = event.target;
    onSameForAllDaysChange(checked);

    if (checked && localSchedule) {
      const firstEnabledDay = DAYS_ORDER.find((day) => localSchedule[day.key]?.enabled);
      if (firstEnabledDay && localSchedule[firstEnabledDay.key]) {
        const firstDaySchedule = localSchedule[firstEnabledDay.key];
        const updatedSchedule = { ...localSchedule };

        DAYS_ORDER.forEach((day) => {
          if (updatedSchedule[day.key]?.enabled) {
            updatedSchedule[day.key] = {
              ...updatedSchedule[day.key],
              startTime: firstDaySchedule.startTime,
              endTime: firstDaySchedule.endTime,
            };
          }
        });

        setLocalSchedule(updatedSchedule);
        onWeekScheduleChange(updatedSchedule);
      }
    }
  };

  const isFirstEnabledDay = (dayKey) => {
    const firstEnabled = DAYS_ORDER.find((day) => localSchedule[day.key]?.enabled);
    return firstEnabled?.key === dayKey;
  };

  const shouldDisableTimeFields = (dayKey) => {
    if (disabled) return true;
    if (!sameForAllDays) return false;
    return !isFirstEnabledDay(dayKey);
  };

  return (
    <div className="weekly-shift-selector">
      <div className="weekly-shift-selector__same-for-all">
        <Checkbox
          checked={sameForAllDays}
          onChange={handleSameForAllDaysChange}
          disabled={disabled}
          sx={{
            color: "var(--color-primitives-neutral-600)",
            "&.Mui-checked": {
              color: "var(--color-surface-action-primary)",
            },
            padding: "0px",
            margin: "9px 0px",
          }}
        />
        <span className="heading-h5-regular weekly-shift-selector__toggle-label">
          My shift timings are same for all days
        </span>
      </div>

      <div className="weekly-shift-selector__days">
        {DAYS_ORDER.map((day) => {
          const daySchedule = localSchedule[day.key] || { enabled: false, startTime: null, endTime: null };
          const timeFieldsDisabled = shouldDisableTimeFields(day.key);

          return (
            <div key={day.key} className="weekly-shift-selector__day-row">
              <div className="weekly-shift-selector__day-toggle">
                <Switch
                  checked={daySchedule.enabled}
                  onChange={() => handleDayToggle(day.key)}
                  disabled={disabled}
                  size="small"
                />
                <span className="heading-h3-medium">{day.label}</span>
              </div>

              <div className="weekly-shift-selector__time-fields">
                {daySchedule.enabled ? (
                  <TimeRangeSelector
                    startTime={daySchedule.startTime}
                    endTime={daySchedule.endTime}
                    onStartTimeChange={(value) => handleTimeChange(day.key, "startTime", value)}
                    onEndTimeChange={(value) => handleTimeChange(day.key, "endTime", value)}
                    startPlaceholder="hh:mm aa"
                    endPlaceholder="hh:mm aa"
                    size="small"
                    disabled={timeFieldsDisabled}
                    showArrow
                    helperText=""
                    startLabel=""
                    endLabel=""
                  />
                ) : (
                  <div className="weekly-shift-selector__off-field">
                    <Text
                      name={`${day.key}-off`}
                      value="Off"
                      size="small"
                      disabled
                      endIcon={<AccessTimeOutlinedIcon className="time-icon" />}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {showHelperText && helperText && (
        <p className="weekly-shift-selector__helper-text para-body-s-medium">{helperText}</p>
      )}
    </div>
  );
}

WeeklyShiftSelector.propTypes = {
  weekSchedule: PropTypes.shape({
    sunday: PropTypes.shape({
      enabled: PropTypes.bool,
      startTime: PropTypes.instanceOf(Date),
      endTime: PropTypes.instanceOf(Date),
    }),
    monday: PropTypes.shape({
      enabled: PropTypes.bool,
      startTime: PropTypes.instanceOf(Date),
      endTime: PropTypes.instanceOf(Date),
    }),
    tuesday: PropTypes.shape({
      enabled: PropTypes.bool,
      startTime: PropTypes.instanceOf(Date),
      endTime: PropTypes.instanceOf(Date),
    }),
    wednesday: PropTypes.shape({
      enabled: PropTypes.bool,
      startTime: PropTypes.instanceOf(Date),
      endTime: PropTypes.instanceOf(Date),
    }),
    thursday: PropTypes.shape({
      enabled: PropTypes.bool,
      startTime: PropTypes.instanceOf(Date),
      endTime: PropTypes.instanceOf(Date),
    }),
    friday: PropTypes.shape({
      enabled: PropTypes.bool,
      startTime: PropTypes.instanceOf(Date),
      endTime: PropTypes.instanceOf(Date),
    }),
    saturday: PropTypes.shape({
      enabled: PropTypes.bool,
      startTime: PropTypes.instanceOf(Date),
      endTime: PropTypes.instanceOf(Date),
    }),
  }).isRequired,
  onWeekScheduleChange: PropTypes.func.isRequired,
  sameForAllDays: PropTypes.bool,
  onSameForAllDaysChange: PropTypes.func.isRequired,
  helperText: PropTypes.string,
  showHelperText: PropTypes.bool,
  disabled: PropTypes.bool,
};

export default WeeklyShiftSelector;
